import { useState, type ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2, ShoppingBag, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { packLabel } from "@/lib/pricing/pack-labels";

/**
 * Quota-pack controls shared by `SubscriptionExpandedCard`.
 *
 * Split out of the card so the card keeps reading as "one subscription's
 * inventory" while the two interactive surfaces (buy a pack, pick the seat a
 * pack goes to) own their own sheet state.
 *
 * Both talk to `/api/features/overage/batch-purchase*` through the card's
 * mutations — this file renders and confirms, it never fetches.
 *
 * Legacy equivalents (`src/components/billing/subscription-expanded-card.tsx`
 * + `src/components/billing/buy-booking-pack-button.tsx`) used an absolutely
 * positioned dropdown for seat selection and a hand-rolled framer-motion
 * modal for the buy confirmation. Both become `<ResponsiveSheet>` here (the
 * mandated primitive): a bottom sheet on mobile, a dialog on desktop, with
 * 44px rows that work at 375px.
 */

// ─── Types ──────────────────────────────────────────────────────────────

/** Subset of `PlanAddonOption` (GET /api/billing/plan-addons) used here. */
export interface PackAddon {
  featureKey: string;
  labelRo: string;
  labelEn: string;
  batchSize: number;
  batchPriceEur: number;
  currency: string;
}

/** One assignable seat — `targetEntityId` is what the API expects. */
export interface SeatChoice {
  id: string;
  targetEntityId: string;
  assigneeLabel: string;
}

type PackLabel = (p: { labelRo: string; labelEn: string }) => string;

// ─── Buy pack row ───────────────────────────────────────────────────────

interface BuyPackRowProps {
  addons: PackAddon[];
  label: PackLabel;
  /** featureKey of the pack currently being purchased, if any. */
  busyFeatureKey: string | null;
  onBuy: (addon: PackAddon) => void;
}

export function BuyPackRow({ addons, label, busyFeatureKey, onBuy }: BuyPackRowProps) {
  const { t, i18n } = useLingui();
  const [confirm, setConfirm] = useState<PackAddon | null>(null);
  // A sellable pack reads its Lingui name in every locale; the caller's label
  // (catalog labelRo / labelEn) stays the fallback for unmapped feature keys.
  const nameOf = (addon: PackAddon) =>
    packLabel(i18n, { featureKey: addon.featureKey, labelEn: label(addon) });

  if (addons.length === 0) return null;

  const busy = confirm ? busyFeatureKey === confirm.featureKey : false;
  const confirmLabel = confirm ? nameOf(confirm) : "";
  const confirmUnits = confirm ? `+${confirm.batchSize}` : "";
  const confirmPrice = confirm ? `${confirm.batchPriceEur} ${confirm.currency}` : "";

  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {addons.map((addon) => {
        const isBusy = busyFeatureKey === addon.featureKey;
        const units = `+${addon.batchSize}`;
        const price = `${addon.batchPriceEur} ${addon.currency}`;
        return (
          <Button
            key={addon.featureKey}
            size="sm"
            variant="outline"
            disabled={isBusy}
            onClick={() => setConfirm(addon)}
            className="h-11 gap-1.5 px-3 sm:h-8"
          >
            {isBusy ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <ShoppingBag className="size-3.5" />
            )}
            {units} {nameOf(addon)} ({price})
          </Button>
        );
      })}

      <ResponsiveSheet
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={<Trans>Confirm purchase</Trans>}
        width="sm"
        closeLabel={t`Close`}
        footer={
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="min-h-[44px] flex-1 text-sm"
              disabled={busy}
              onClick={() => setConfirm(null)}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              className="min-h-[44px] flex-1 text-sm"
              disabled={busy}
              onClick={() => {
                if (confirm) onBuy(confirm);
                setConfirm(null);
              }}
            >
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Trans>Buy for {confirmPrice}</Trans>
              )}
            </Button>
          </div>
        }
      >
        <p className="text-muted-foreground text-sm">
          <Trans>
            {confirmUnits} {confirmLabel} for {confirmPrice} per billing period. The pack lands in
            this subscription's inventory and renews automatically every period until you stop its
            renewal.
          </Trans>
        </p>
      </ResponsiveSheet>
    </div>
  );
}

// ─── Seat picker ────────────────────────────────────────────────────────

interface SeatPickerSheetProps {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  seats: SeatChoice[];
  busy: boolean;
  onPick: (targetEntityId: string) => void;
  onClose: () => void;
}

export function SeatPickerSheet({
  open,
  title,
  description,
  seats,
  busy,
  onPick,
  onClose,
}: SeatPickerSheetProps) {
  const { t } = useLingui();

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      width="sm"
      closeLabel={t`Close`}
    >
      {seats.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          <Trans>No active seats to assign this pack to.</Trans>
        </p>
      ) : (
        <div className="space-y-2">
          {seats.map((seat) => (
            <button
              key={seat.id}
              type="button"
              disabled={busy}
              onClick={() => onPick(seat.targetEntityId)}
              className="border-border hover:bg-muted flex min-h-[44px] w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:opacity-50"
            >
              <Users className="text-muted-foreground size-4 shrink-0" />
              <span className="truncate">{seat.assigneeLabel}</span>
            </button>
          ))}
        </div>
      )}
    </ResponsiveSheet>
  );
}
